import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import SpellTextarea from '../spellcheck/SpellTextarea';
import './ClientInfo.css';

export default function ClientInfo({ clientId, visible, onClose }) {
  const [data, setData] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await api.get('/clients/' + clientId + '/info');
      setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [clientId]);

  useEffect(() => {
    if (visible && clientId) load();
  }, [visible, clientId, load]);

  const addAnnotation = async () => {
    if (!newNote.trim()) return;
    try {
      const res = await api.post('/clients/' + clientId + '/annotations', { body: newNote });
      setData((prev) => ({
        ...prev,
        annotations: [res.data, ...(prev?.annotations || [])],
      }));
      setNewNote('');
    } catch (e) { console.error(e); }
  };

  const deleteAnnotation = async (id) => {
    try {
      await api.delete('/clients/' + clientId + '/annotations/' + id);
      setData((prev) => ({
        ...prev,
        annotations: (prev?.annotations || []).filter((a) => a.id !== id),
      }));
    } catch (e) { console.error(e); }
  };

  const formatAge = (birthDate) => {
    if (!birthDate) return '';
    const birth = new Date(birthDate);
    const now = new Date();
    let years = now.getFullYear() - birth.getFullYear();
    let months = now.getMonth() - birth.getMonth();
    if (months < 0) { years--; months += 12; }
    if (years > 0) return years + ' ' + (years === 1 ? 'год' : years < 5 ? 'года' : 'лет');
    return months + ' мес.';
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });

  if (!visible) return null;

  return (
    <div className="client-info-panel">
      <div className="client-info-header">
        <span>Карточка клиента</span>
        <button onClick={onClose}>&times;</button>
      </div>

      {loading && !data ? (
        <div className="client-info-loading">Загрузка...</div>
      ) : data ? (
        <div className="client-info-body">
          <div className="client-info-section">
            <div className="client-info-name">{data.client.name}</div>
            <div className="client-info-email">{data.client.email}</div>
          </div>

          {data.pets.length > 0 && (
            <div className="client-info-section">
              <div className="client-info-label">Питомцы</div>
              {data.pets.map((pet) => (
                <div key={pet.id} className="client-info-pet">
                  <span className="pet-name">{pet.name}</span>
                  {pet.species && <span className="pet-species">{pet.species}</span>}
                  {pet.birth_date && <span className="pet-age">{formatAge(pet.birth_date)}</span>}
                </div>
              ))}
            </div>
          )}

          <div className="client-info-section">
            <div className="client-info-label">Заметки врачей</div>
            <div className="client-info-add-note">
              <SpellTextarea
                value={newNote}
                onChange={(v) => setNewNote(v)}
                placeholder="Добавить заметку..."
                rows={2}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addAnnotation(); } }}
                className="client-info-note-input"
              />
              <button onClick={addAnnotation}>+</button>
            </div>
            <div className="client-info-notes">
              {(data.annotations || []).map((a) => (
                <div key={a.id} className="client-info-note">
                  <div className="note-text">{a.body}</div>
                  <div className="note-meta">
                    <span>{a.author_name}</span>
                    <span>{formatDate(a.created_at)}</span>
                    <button onClick={() => deleteAnnotation(a.id)} title="Удалить">&times;</button>
                  </div>
                </div>
              ))}
              {(!data.annotations || data.annotations.length === 0) && (
                <div className="client-info-empty">Нет заметок</div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
